
import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useOrganizationModels } from "@/components/settings/model-preferences/hooks/useOrganizationModels";
import { useAuth } from "@/components/auth/AuthProvider";
import { Info, Loader2, AlertCircle, Languages } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CustomModelSelectorProps {
  generatorModel?: string;
  improverModel?: string;
  onGeneratorModelChange: (model: string | undefined) => void;
  onImproverModelChange: (model: string | undefined) => void;
}

export const CustomModelSelector = ({
  generatorModel,
  improverModel,
  onGeneratorModelChange,
  onImproverModelChange,
}: CustomModelSelectorProps) => {
  const { organization } = useAuth();
  const { data: organizationModels, isLoading: isLoadingOrgModels } = useOrganizationModels(organization?.id);
  const [useCustomModels, setUseCustomModels] = useState(false);

  // Use the same model for both generator and improver
  const selectedModel = generatorModel || improverModel;

  // Initialize custom models state based on props
  useEffect(() => {
    if (generatorModel || improverModel) {
      console.log('CustomModelSelector: Setting useCustomModels to true because models are present:', {
        generatorModel,
        improverModel
      });
      setUseCustomModels(true);
    }
  }, [generatorModel, improverModel]);

  // Available models (only organization fine-tuned models)
  const availableModels = organizationModels || [];

  // Helper function to get user-friendly model label
  const getModelLabel = (modelValue: string | undefined) => {
    if (!modelValue) {
      return "Organization default";
    }

    if (isLoadingOrgModels) {
      return "Loading...";
    }
    
    const model = availableModels.find(m => m.value === modelValue);
    return model ? model.label : modelValue;
  };

  const handleCustomModelsToggle = (enabled: boolean) => {
    console.log('CustomModelSelector: Toggling custom models to:', enabled);
    setUseCustomModels(enabled);
    if (!enabled) {
      // Reset to defaults when disabling custom models
      onGeneratorModelChange(undefined);
      onImproverModelChange(undefined);
    }
  };

  // Helper function to convert undefined to "default" for Select value
  const getSelectValue = (modelValue: string | undefined) => {
    return modelValue || "default";
  };

  // Helper function to handle model changes for both generator and improver
  const handleModelChange = (value: string) => {
    const newValue = value === "default" ? undefined : value;
    console.log('CustomModelSelector: Model changed to:', newValue);
    // Set the same model for both generator and improver
    onGeneratorModelChange(newValue);
    onImproverModelChange(newValue);
  };

  // Show message if no fine-tuned models are available
  const hasFineTunedModels = availableModels.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Custom AI Model Selection</CardTitle>
            <CardDescription className="text-sm">
              Use your organization's fine-tuned models for both content generation and improvement
            </CardDescription>
          </div>
          <Switch
            checked={useCustomModels}
            onCheckedChange={handleCustomModelsToggle}
            disabled={!hasFineTunedModels && !isLoadingOrgModels}
          />
        </div>
      </CardHeader>
      
      {useCustomModels && (
        <CardContent className="pt-0 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              The selected fine-tuned model will be used for both content generation and improvement in this collection.
            </p>
          </div>
          
          <div className="flex items-start gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <Languages className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-purple-800">
              This model will also be used for all translations of this collection, ensuring consistency across languages.
            </p>
          </div>

          {isLoadingOrgModels && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />
              <p className="text-sm text-gray-600">Loading fine-tuned models...</p>
            </div>
          )}

          {!isLoadingOrgModels && !hasFineTunedModels && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No fine-tuned models available. Create fine-tuned models in the Tonality section to use custom models.
              </AlertDescription>
            </Alert>
          )}

          {!isLoadingOrgModels && hasFineTunedModels && (
            <div className="space-y-2">
              <Label htmlFor="custom-model">Fine-tuned Model</Label>
              <Select 
                value={getSelectValue(selectedModel)} 
                onValueChange={handleModelChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Use organization default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Use organization default</SelectItem>
                  {availableModels.map((model) => (
                    <SelectItem key={model.value} value={model.value}>
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      )}

      {/* Display section for configured models - only show when not in edit mode and org models are loaded */}
      {selectedModel && !useCustomModels && !isLoadingOrgModels && (
        <CardContent className="pt-0">
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <h4 className="text-sm font-medium text-green-800 mb-2">Custom AI Model Configured</h4>
            <div className="space-y-1 text-sm text-green-700">
              <div>
                <span className="font-medium">Model:</span> {getModelLabel(selectedModel)}
              </div>
              <p className="text-xs text-green-600 mt-1">
                Applied to both content generation and improvement
              </p>
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Languages className="h-3 w-3" />
                Also used for all translations of this collection
              </p>
            </div>
          </div>
        </CardContent>
      )}

      {/* Loading state for display section */}
      {selectedModel && !useCustomModels && isLoadingOrgModels && (
        <CardContent className="pt-0">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />
              <h4 className="text-sm font-medium text-gray-600">Loading Custom AI Model Configuration...</h4>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
